/*
* Copyright (c) 2009-2022. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

// Per-tenant chat enablement (chat-claim-gate), driven by the Keycloak `zaya_modules` token claim.
// The claim-parsing logic lives in `@zaya-chat/core`'s `moduleCapability.ts` — shared with LIS
// (zaya-lis-fe/src/utils/module-capability.ts) so the two hosts cannot drift on what "enabled"
// means. IMS's only job here is retrieving its OWN token: `Vue.$keycloak` (installed by
// `keycloak.js`, the same singleton `token-utils.js`'s `updateToken()` calls) already decodes the
// JWT for us onto `.tokenParsed` — no manual base64 decode needed on this side (LIS decodes
// manually because its session is a raw JWT string in localStorage, not a keycloak-js instance).
//
// Fails CLOSED (unlike `zaya_visualizers`, which has no IMS equivalent): chat is opt-in per-tenant
// provisioning, so an absent/unparseable claim must hide the chat island and its top-bar entry
// entirely, not just disable them.
import Vue from 'vue';
import { modulesFromToken } from '@zaya-chat/core';

/** Whether the CURRENT Keycloak session's token grants the `chat` module. Read at call time — not
 *  cached — so it always reflects whatever token is actually live right now. */
export function isChatEnabled() {
  const tokenParsed = Vue.$keycloak && Vue.$keycloak.tokenParsed;
  if (!tokenParsed) {
    return false;
  }
  return modulesFromToken(tokenParsed).chat;
}
